import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@legal-500/legal500-design-system";
import { fetchAllEditionBpas, researchCms } from "@/api/researchCms.service";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { usePermissions } from "@/hooks/usePermissions";
import { CmsConfirmDialog } from "../../../components/ResearchCms/CmsConfirmDialog/CmsConfirmDialog";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { isEditionLocked } from "./utils/bpaImportEditionStatus";
import { CmsLayout } from "./components/CmsLayout";
import { messageOf } from "./components/BpaImport/importText";
import { CmsBpaMergeAddDialog } from "./components/BpaMerge/CmsBpaMergeAddDialog";
import { ALL_KINDS, type ImportKind, type ImportPreviewOrg } from "./utils/bpaImport";
import {
  mergeArchiveConfirmation,
  holdingsFromSummary,
  mergeAddAction,
  mergeHubModel,
  mergeFinishOutcome,
  mergeHubPermissions,
  mergeImportUrl,
  mergeRemoveConfirmation,
  type MergeCard,
  type MergeHoldings,
  toMergeRow,
  type MergeRow,
} from "./utils/bpaMerge";
import hub from "./CmsBpaHub.module.css";
import styles from "./CmsBpaMergeHubPage.module.css";
import shared from "./components/BpaImport/importShared.module.css";

const HUB_QUERY_KEYS = [
  ["merge-hub-base"],
  ["import-history"],
  ["merge-hub-holdings"],
  ["research-edition-bpas"],
  ["research-bpa-detail"],
];

type RemoveInput = {
  editionBpaId: string;
  endsMerge: boolean;
};

type FinishInput = {
  sourceEditionBpaIds: string[];
  mergedSources: string[];
  mergedBroughtIn: string[];
  mergedKeptOut: string[];
};

const ARCHIVE_REASON_ID = "merge-archive-reason";
const VIEW_ONLY_REASON = "You have view access only.";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB");
}

function itemCount(org: ImportPreviewOrg): number {
  return Object.values(org.sourceCounts).reduce((sum, count) => sum + count, 0);
}

function holdingsOf(orgs: ImportPreviewOrg[]): MergeHoldings {
  const withContent = orgs.filter(org => itemCount(org) > 0);
  return {
    organisations: withContent.filter(org => !org.noFirm).length,
    items: withContent.reduce((sum, org) => sum + itemCount(org), 0),
    noFirm: withContent.some(org => org.noFirm),
  };
}

export function CmsBpaMergeHubPage() {
  const { editionId = "", bpaId = "" } = useParams<{ editionId: string; bpaId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const [confirming, setConfirming] = useState(false);
  const archiveButtonRef = useRef<HTMLButtonElement>(null);
  const [removingEditionBpaId, setRemovingEditionBpaId] = useState<string | null>(null);
  const removeButtonRef = useRef<HTMLElement | null>(null);
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const flagOn = hasFlag("research-cms-1-enabled");
  const canRead = hasPermission("staff.research.read");
  const canEdit = hasPermission("staff.research.edit");
  const canDelete = hasPermission("staff.research.delete");
  const gateOpen = flagOn && canRead;

  const baseQuery = useQuery({
    queryKey: ["merge-hub-base", editionId],
    enabled: gateOpen,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const [editionBpas, editions] = await Promise.all([
        fetchAllEditionBpas({ editionId, includeArchived: true }),
        researchCms.getEditions({ editionId, limit: 1 }),
      ]);
      const edition = editions.editionConfigs[0];
      return {
        rows: editionBpas.map(toMergeRow),
        rankingEntity: (() => {
          const own = editionBpas.find(row => row.bpaId === bpaId);
          return own?.rankingEntityName
            ? { id: own.rankingEntityId, name: own.rankingEntityName }
            : null;
        })(),
        editionName: edition?.editionName ?? "",
        editionStatus: edition?.status ?? null,
      };
    },
  });

  const rows = baseQuery.data?.rows;
  const target = rows?.find(row => row.bpaId === bpaId) ?? null;
  const rankingEntity = baseQuery.data?.rankingEntity ?? null;
  const editionStatus = baseQuery.data?.editionStatus ?? null;

  const historyQuery = useQuery({
    queryKey: ["import-history", target?.id],
    enabled: gateOpen && !!target,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: () => researchCms.getImportHistory(target!.id),
  });
  const history = historyQuery.data?.importHistory;

  const scheduled = (rows ?? []).filter(
    row => target && row.mergeTargetEditionBpaId === target.id && row.status !== "archived",
  );
  const holdingsQuery = useQuery({
    queryKey: ["merge-hub-holdings", target?.id, editionStatus, scheduled.map(row => row.id)],
    enabled: gateOpen && !!target,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      if (isEditionLocked(editionStatus)) {
        const lockedEntries = await Promise.all(
          scheduled.map(async row => {
            const summary = await researchCms.getContentSummary(row.id);
            return [row.id, holdingsFromSummary(summary.firms)] as const;
          }),
        );
        return Object.fromEntries(lockedEntries) as Record<string, MergeHoldings>;
      }
      const entries = await Promise.all(
        scheduled.map(async row => {
          const preview = await researchCms.getImportPreview(target!.id, {
            sourceEditionBpaId: row.id,
            contentTypes: ALL_KINDS,
            excludeCompanyIds: [],
            excludeNoFirm: false,
            clashResolution: {},
          });
          return [row.id, holdingsOf(preview.orgs)] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<string, MergeHoldings>;
    },
  });

  function refreshHub() {
    return Promise.all(HUB_QUERY_KEYS.map(queryKey => queryClient.invalidateQueries({ queryKey })));
  }

  const removeMutation = useMutation({
    mutationFn: (input: RemoveInput) => researchCms.removeFromMerge(input.editionBpaId),
    onSuccess: async (_result, { endsMerge }) => {
      if (endsMerge) {
        navigate(ROUTES.research.cmsBpa(editionId, bpaId));
        void refreshHub();
        return;
      }
      await refreshHub();
    },
    onError: error => setActionError(messageOf(error)),
  });
  const archiveMutation = useMutation({
    mutationFn: (input: FinishInput) =>
      researchCms.finishMerge(target!.id, input.sourceEditionBpaIds),
    onSuccess: (_result, { mergedSources, mergedBroughtIn, mergedKeptOut }) => {
      navigate(ROUTES.research.cmsBpa(editionId, bpaId), {
        state: { mergedSources, mergedBroughtIn, mergedKeptOut },
      });
      void refreshHub();
    },
    onError: error => {
      setActionError(messageOf(error));
      void refreshHub();
    },
  });
  const busy = removeMutation.isPending || archiveMutation.isPending;

  if (flagsLoading) return null;

  const permissions = mergeHubPermissions({ flagOn, canRead, canEdit, canDelete, editionStatus });
  if (!permissions.open) return <UnauthorizedView />;

  const failed = baseQuery.isError || historyQuery.isError || holdingsQuery.isError;
  const loadError = baseQuery.error ?? historyQuery.error ?? holdingsQuery.error;
  const retry = () => {
    if (baseQuery.isError) baseQuery.refetch();
    if (historyQuery.isError) historyQuery.refetch();
    if (holdingsQuery.isError) holdingsQuery.refetch();
  };

  const targetName = target?.bpaName ?? "";
  const breadcrumbs = [
    {
      label: baseQuery.data?.editionName || "Edition",
      path: ROUTES.research.cmsPublication(editionId),
    },
    ...(rankingEntity
      ? [
          {
            label: rankingEntity.name,
            path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${rankingEntity.id}`,
          },
        ]
      : []),
    { label: targetName || "Practice area", path: ROUTES.research.cmsBpa(editionId, bpaId) },
    { label: "Merge" },
  ];

  if (failed) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <div className={hub.error} role="alert">
          <span>{messageOf(loadError)}</span>
          <Button variant="outlined" color="default" size="small" onClick={retry}>
            Retry
          </Button>
        </div>
      </CmsLayout>
    );
  }

  if (rows && !target) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <p className={hub.status}>This practice area is not in this edition.</p>
        <Link to={ROUTES.research.cmsPublication(editionId)}>Back to the edition</Link>
      </CmsLayout>
    );
  }

  const holdings = holdingsQuery.data;
  if (!target || !rows || !history || !holdings) {
    return (
      <CmsLayout breadcrumbs={breadcrumbs}>
        <p className={hub.status} role="status">
          Loading...
        </p>
      </CmsLayout>
    );
  }

  const knownHoldings: Record<string, MergeHoldings> = holdings;
  const model = mergeHubModel({ target, rows, history, holdings, editionStatus });
  const importReason = permissions.viewOnly ? VIEW_ONLY_REASON : permissions.importDisabledReason;
  const removeReason = permissions.viewOnly ? VIEW_ONLY_REASON : permissions.removeDisabledReason;
  const archiveReason = permissions.viewOnly ? VIEW_ONLY_REASON : permissions.archiveDisabledReason;
  const addAction = mergeAddAction({ cards: model.cards, canDelete, editionStatus });
  const addReason = permissions.viewOnly ? VIEW_ONLY_REASON : addAction.disabledReason;
  const confirmation = mergeArchiveConfirmation({ cards: model.cards, holdings });
  const warning = confirmation[confirmation.length - 1];
  const details = confirmation.slice(0, -1);

  const removing = model.cards.find(card => card.editionBpaId === removingEditionBpaId) ?? null;
  const removeConfirmation = removing
    ? mergeRemoveConfirmation({ card: removing, cards: model.cards, targetName })
    : null;

  function askToRemove(card: MergeCard, trigger: HTMLElement) {
    removeButtonRef.current = trigger;
    setRemovingEditionBpaId(card.editionBpaId);
  }

  function closeAddDialog(added: boolean) {
    setAdding(false);
    if (added) void refreshHub();
  }

  function removeCard(card: MergeCard, endsMerge: boolean) {
    setRemovingEditionBpaId(null);
    setActionError(null);
    removeMutation.mutate({ editionBpaId: card.editionBpaId, endsMerge });
  }

  async function requestArchive() {
    const refreshed = await holdingsQuery.refetch();
    if (refreshed.isError) return;
    setConfirming(true);
  }

  function archiveSources() {
    setConfirming(false);
    setActionError(null);
    const { sourceNames, outcome } = mergeFinishOutcome({
      cards: model.cards,
      holdings: knownHoldings,
    });
    archiveMutation.mutate({
      sourceEditionBpaIds: model.archiveEditionBpaIds,
      mergedSources: sourceNames,
      mergedBroughtIn: outcome.broughtIn,
      mergedKeptOut: outcome.keptOut,
    });
  }

  const archiveButton = model.archiveOffered && (
    <Button
      ref={archiveButtonRef}
      className={shared.compactButton}
      variant="contained"
      color="primary"
      disabled={busy || archiveReason !== null}
      title={archiveReason ?? undefined}
      aria-describedby={archiveReason ? ARCHIVE_REASON_ID : undefined}
      onClick={requestArchive}
    >
      {archiveMutation.isPending ? "Archiving..." : model.archiveButtonLabel}
    </Button>
  );

  return (
    <CmsLayout breadcrumbs={breadcrumbs}>
      <div className={hub.page}>
        <div className={hub.column}>
          <div className={hub.heading}>
            <h1 className={hub.title}>Merge into {targetName}</h1>
            <p className={hub.meta}>
              {[baseQuery.data?.editionName, rankingEntity?.name].filter(Boolean).join(" · ")}
            </p>
            <p className={hub.explainer}>
              {`Import each practice area below into ${targetName}. When all are done, archive them to finish the merge.`}
            </p>
          </div>

          <section className={hub.box} aria-labelledby="merge-cards-heading">
            <div className={hub.boxHeader}>
              <h2 id="merge-cards-heading" className={hub.micro}>
                Merging from
              </h2>
              <span className={hub.count}>{model.cards.length}</span>
            </div>
            {model.emptyText && (
              <>
                <p className={hub.muted}>{model.emptyText}</p>
                <div className={styles.emptyActions}>
                  <Button
                    variant="outlined"
                    color="default"
                    size="small"
                    className={shared.cardButton}
                    onClick={() => navigate(ROUTES.research.cmsBpa(editionId, bpaId))}
                  >
                    Back to {targetName}
                  </Button>
                </div>
              </>
            )}
            <div className={hub.cards}>
              {model.cards.map(card => (
                <div key={card.editionBpaId} className={hub.card}>
                  <div className={hub.cardName}>
                    {card.done && (
                      <span className={hub.doneMark} aria-hidden="true">
                        ✓
                      </span>
                    )}
                    {card.name}
                  </div>
                  {card.importedAt ? (
                    <p className={hub.cardLine}>{card.statusLine}</p>
                  ) : (
                    <div className={hub.muted}>{card.statusLine}</div>
                  )}
                  {card.importedAt && (
                    <div className={hub.muted}>{formatDate(card.importedAt)}</div>
                  )}
                  {card.archived && <span className={styles.archivedTag}>Archived</span>}
                  <div className={styles.cardActions}>
                    {card.importVisible && importReason === null && (
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        className={shared.cardButton}
                        onClick={() =>
                          navigate(
                            mergeImportUrl({
                              editionId,
                              targetBpaId: bpaId,
                              sourceEditionBpaId: card.editionBpaId,
                            }),
                          )
                        }
                      >
                        {card.importLabel}
                      </Button>
                    )}
                    {card.importVisible && importReason !== null && (
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        className={shared.cardButton}
                        disabled
                        title={importReason}
                      >
                        {card.importLabel}
                      </Button>
                    )}
                    {card.removeVisible && (
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        className={shared.cardButton}
                        disabled={busy || removeReason !== null}
                        title={removeReason ?? undefined}
                        onClick={event => askToRemove(card, event.currentTarget)}
                      >
                        Remove from merge
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {importReason && model.cards.some(card => card.importVisible) && (
              <p className={hub.reason}>{importReason}</p>
            )}
            {addAction.offered && (
              <div className={styles.addActions}>
                <Button
                  variant="outlined"
                  color="default"
                  size="small"
                  className={shared.cardButton}
                  disabled={busy || addReason !== null}
                  title={addReason ?? undefined}
                  onClick={() => setAdding(true)}
                >
                  Add practice area
                </Button>
              </div>
            )}
          </section>

          {actionError && (
            <div className={hub.error} role="alert">
              <span>{actionError}</span>
            </div>
          )}
        </div>

        <div className={hub.footer}>
          <span className={hub.progress}>{model.progress}</span>
          <span className={hub.footerActions}>
            {archiveReason && model.archiveOffered && (
              <span id={ARCHIVE_REASON_ID} className={hub.reason}>
                {archiveReason}
              </span>
            )}
            <Button
              className={shared.compactButton}
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.research.cmsBpa(editionId, bpaId))}
            >
              Close
            </Button>
            {archiveButton}
          </span>
        </div>
      </div>

      {confirming && model.archiveLabel && (
        <CmsConfirmDialog
          title={model.archiveLabel}
          message={warning}
          detail={
            details.length > 0 ? (
              <ul className={hub.confirmList}>
                {details.map(line => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : undefined
          }
          confirmLabel="Archive"
          cancelLabel="Cancel"
          tone="warning"
          returnFocusTo={archiveButtonRef}
          onConfirm={archiveSources}
          onCancel={() => setConfirming(false)}
        />
      )}

      {adding && (
        <CmsBpaMergeAddDialog
          editionId={editionId}
          target={target}
          cards={model.cards}
          rows={rows}
          onClose={closeAddDialog}
        />
      )}

      {removing && removeConfirmation && (
        <CmsConfirmDialog
          title={removeConfirmation.title}
          message={removeConfirmation.message}
          confirmLabel="Remove from merge"
          cancelLabel="Cancel"
          tone="warning"
          returnFocusTo={removeButtonRef}
          onConfirm={() => removeCard(removing, removeConfirmation.endsMerge)}
          onCancel={() => setRemovingEditionBpaId(null)}
        />
      )}
    </CmsLayout>
  );
}
